import * as Sentry from "@sentry/react";
import {
    AccountSubtype,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import {
    getAccountClassForType,
    isAccountType,
} from "@common/constants/domains/accounting/helpers";
import { EMPTY_GROUP_SORT_ORDER_SENTINEL } from "@common/constants/domains/accounting/accountGroups";
import { AccountGroup } from "@common/types/domains/accounting/account";
import {
    AccountMergeStatus,
    PreviewAccountChange,
    PreviewAccountOrigin,
} from "@common/types/domains/accounting/chartOfAccountsTemplateApply";
import { ExtendedAccount } from "@common/types/reports/types";
import type { ChartOfAccountsTemplatePreviewEntity } from "@common/schemas/chart-of-accounts-template-apply.schema";

// Sentinel code for a preview account whose payload `code` is null. Sorts last
// (so an account with no code never jumps to the top like `0` would) and signals
// the name cell to omit the "{code} - " prefix. Preview accounts effectively
// always carry a code, so this is purely defensive.
export const PREVIEW_MISSING_CODE = Number.MAX_SAFE_INTEGER;

// Preview-only extension of the live-CoA account shape, carrying the merge
// status + diff fields so the preview can render the Added / Reassigned / Moved
// tags and the Updates filter can narrow by them. The shared
// `ExtendedAccount`/`Account` are NOT mutated.
export type PreviewExtendedAccount = ExtendedAccount & {
    mergeStatus: AccountMergeStatus;
    // The code the account would have had before the merge changed it (e.g. a
    // clash bumped a new account to the first-available code, or an update changed
    // an existing account's code). Distinct from the displayed `code`; null when
    // unchanged.
    previousCode: number | null;
    // Where the row sits in the template <> entity diff (drives the Origin filter).
    origin: PreviewAccountOrigin;
    // Material changes the apply would make (drives tags + the Updates filter).
    changes: PreviewAccountChange[];
    // Current archived state remains on `isDisabled`; this field is true only
    // when the apply itself transitions an existing account to archived.
    wasArchivedByApply: boolean;
    // Breadcrumb of the group this account moves out of, set only when reassigned.
    previousGroupPath: string | null;
};

// Surfaces FE/BE account-type enum drift: a row whose `type` the frontend can't
// recognise is skipped (defensive), but we report it so it's debuggable rather
// than silently vanishing from the preview tree.
function reportUnknownType(context: "account" | "group", type: string): void {
    Sentry.captureMessage(
        `CoA apply preview: skipped ${context} with unknown type "${type}"`,
        (scope) =>
            scope
                .setLevel("warning")
                .setTag("coaApplyPreview", "unknownType")
                .setTag("coaApplyPreviewContext", context),
    );
}

interface PreviewCoaInputs {
    accounts: PreviewExtendedAccount[];
    groups: AccountGroup[];
    // Ids of groups the apply would create (in the template, not yet on the
    // entity). Drives the group "Added" tag + the "Added group" filter.
    newGroupIds: Set<string>;
    groupOriginById: Map<string, PreviewAccountOrigin>;
}

function narrowAccountType(value: string): AccountType | null {
    if (isAccountType(value)) {
        return value;
    }
    return null;
}

// Fallback subtype keeps the row sortable/typed when the payload subtype is
// unknown. Subtype only affects display + the no-account-numbers sort fallback,
// so any valid member is safe.
export const FALLBACK_SUBTYPE: AccountSubtype =
    AccountSubtype.otherCurrentAssets;

function narrowAccountSubtype(value: string | null): AccountSubtype {
    if (!value) {
        return FALLBACK_SUBTYPE;
    }
    const v2 = Object.values(AccountSubtype).find(
        (subtype) => subtype === value,
    );
    if (v2) {
        return v2;
    }
    return FALLBACK_SUBTYPE;
}

// Maps a preview entity payload into the inputs `buildHierarchicalChartOfAccounts`
// expects (live-CoA shape) plus the per-account merge status. `class` is
// recomputed from `type` so the builder's classification logic stays correct
// regardless of the payload's `class` string.
export function mapPreviewEntityToCoaInputs(
    entity: ChartOfAccountsTemplatePreviewEntity,
): PreviewCoaInputs {
    const accounts: PreviewExtendedAccount[] = [];
    for (const account of entity.accounts) {
        const accountType = narrowAccountType(account.type);
        if (!accountType) {
            reportUnknownType("account", account.type);
            continue;
        }
        accounts.push({
            id: account.id,
            code: account.code ?? PREVIEW_MISSING_CODE,
            codeText: account.codeText,
            name: account.name,
            class: getAccountClassForType(accountType),
            type: accountType,
            subtype: narrowAccountSubtype(account.subtype),
            isDisabled: account.isDisabled,
            isManuallyCreated: account.isManuallyCreated,
            financialAccountId: null,
            kickOriginalCode: null,
            parentGroupId: account.parentGroupId,
            sortOrder: account.sortOrder,
            debit: 0,
            credit: 0,
            balance: 0,
            entityId: entity.entityId,
            hasOpeningBalance: false,
            mergeStatus: account.mergeStatus,
            previousCode: account.previousCode,
            origin: account.origin,
            changes: account.changes,
            wasArchivedByApply: account.changes.includes(
                PreviewAccountChange.archived,
            ),
            previousGroupPath: account.previousGroupPath,
        });
    }

    const groups: AccountGroup[] = [];
    const newGroupIds = new Set<string>();
    const groupOriginById = new Map<string, PreviewAccountOrigin>();
    for (const group of entity.groups) {
        const groupType = narrowAccountType(group.type);
        if (!groupType) {
            reportUnknownType("group", group.type);
            continue;
        }
        if (group.isNew) {
            newGroupIds.add(group.id);
        }
        groupOriginById.set(group.id, group.origin);
        groups.push({
            id: group.id,
            entityId: entity.entityId,
            name: group.name,
            type: groupType,
            parentGroupId: group.parentGroupId,
            sortOrder: EMPTY_GROUP_SORT_ORDER_SENTINEL,
        });
    }
    projectGroupSortOrders({ accounts, groups });

    return { accounts, groups, newGroupIds, groupOriginById };
}

function projectGroupSortOrders({
    accounts,
    groups,
}: Pick<PreviewCoaInputs, "accounts" | "groups">): void {
    const groupsById = new Map(groups.map((group) => [group.id, group]));
    for (const account of accounts) {
        const visitedGroupIds = new Set<string>();
        let groupId = account.parentGroupId;
        while (groupId !== null) {
            const group = groupsById.get(groupId);
            if (!group || visitedGroupIds.has(group.id)) {
                break;
            }
            visitedGroupIds.add(group.id);
            group.sortOrder = Math.min(group.sortOrder, account.sortOrder);
            groupId = group.parentGroupId;
        }
    }
}
